/**
 * PAWNED AI (spec §20). It sees only view(state, side), chooses only from legalActions, and is
 * deterministic for a given seed. Simulations run on determinized copies of the view.
 */
import {
  Pcg32,
  actionKey,
  cardCode,
  hasMove,
  boardCtx,
  onBoardPawns,
  opponent,
  parseHex64,
  type Action,
  type Face,
  type MatchState,
  type MatchView,
  type Side,
} from '../engine';
import { allCards, determinize } from './determinize';
import { BALANCED, DIFFICULTY_SETTINGS, type Difficulty, type Personality } from './personality';
import { preferNewInscription, searchPlans, type Plan } from './planner';
import { refinePlans } from './refine';
import { passOrContinue } from './rounds';
import { cardValueFor, epitaphValue, observedRate, poolOf, type EvalContext } from './valuation';

export * from './personality';
export { determinize, unseenCards } from './determinize';
export { roundSituation, estimateChase } from './rounds';

export interface AiOptions {
  readonly difficulty?: Difficulty;
  readonly personality?: Personality;
  /** 64-bit hex seed for this decision (see aiDecisionSeed). */
  readonly seed: string;
}

export interface AiDecision {
  readonly action: Action;
  /** Short machine-readable explanation, for logs and tests. */
  readonly reason: string;
  readonly nodes: number;
}

/** Per-decision AI seed derived from the match's AI stream (§16.2); needs the full state, so hosts call it. */
export function aiDecisionSeed(state: Pick<MatchState, 'rng' | 'seq'>, side: Side): string {
  const rng = Pcg32.seeded(
    parseHex64(state.rng.ai.seed),
    BigInt(state.seq * 2 + (side === 'S' ? 0 : 1)),
  );
  return rng.nextU64().toString(16).padStart(16, '0');
}

function contextFor(v: MatchView, personality: Personality, difficulty: Difficulty): EvalContext {
  const me = v.viewer;
  const opp = opponent(me);
  const oppView = v.players[opp];
  const oppHand = Math.min(
    v.config.constants.HAND_LIMIT,
    oppView.handCount + (oppView.deckCount > 0 ? 1 : 0),
  );
  return {
    me,
    opp,
    personality,
    settings: DIFFICULTY_SETTINGS[difficulty],
    oppPool: poolOf(v, opp),
    oppHand,
    cardValue: cardValueFor(v, me, personality),
    rate: observedRate(v),
  };
}

/** Usefulness of a card in hand, for choosing what to Recover. */
function cardUtility(s: MatchState, me: Side, cardId: string): number {
  const card = s.cards[cardId];
  if (!card) return 0;
  if (card.kind === 'wild') return 4;
  const ctx = boardCtx(s);
  const movers = onBoardPawns(s.pawns, me).filter((p) => hasMove(ctx, p, card.face as Face)).length;
  const dupes = s.players[me].hand.filter(
    (id) => s.cards[id] && cardCode(s.cards[id]!) === cardCode(card),
  ).length;
  return 1 + movers * 0.35 - (dupes - 1) * 0.4;
}

function pickPlan(plans: Plan[], temperature: number, rng: Pcg32, blunder = 0): Plan | undefined {
  if (plans.length === 0) return undefined;
  // A blunder: any plan it saw, good or bad, as a person misreads the board now and then.
  if (blunder > 0 && rng.nextFloat() < blunder) return plans[rng.nextBelow(plans.length)];
  if (temperature <= 0) return plans[0];
  const top = plans.slice(0, 12);
  const max = top[0]!.value;
  const weights = top.map((p) => Math.exp((p.value - max) / temperature));
  const total = weights.reduce((a, b) => a + b, 0);
  let r = rng.nextFloat() * total;
  for (let i = 0; i < top.length; i++) {
    r -= weights[i]!;
    if (r <= 0) return top[i];
  }
  return top[top.length - 1];
}

/** Chooses the next action of a turn from the best complete plan. */
function planAction(
  v: MatchView,
  ctx: EvalContext,
  rng: Pcg32,
): { action: Action; reason: string; nodes: number } {
  const state = determinize(v, rng);
  const { plans, nodes } = searchPlans(state, ctx);
  for (const p of plans)
    if (p.kind === 'recover') {
      const a = p.actions[0] as Extract<Action, { type: 'RECOVER' }>;
      const utilities = state.players[ctx.me].hand.map((id) => cardUtility(state, ctx.me, id));
      const avg = utilities.reduce((x, y) => x + y, 0) / Math.max(1, utilities.length);
      p.value += (avg - cardUtility(state, ctx.me, a.cardId)) * ctx.cardValue * 0.5;
    }
  plans.sort((a, b) => b.value - a.value);
  const refineNodes = refinePlans(plans, ctx, rng);
  const plan = pickPlan(plans, ctx.settings.temperature, rng, ctx.settings.blunder);
  const first = plan?.actions[0];
  if (!plan || !first)
    return { action: { type: 'END_CHAIN', side: ctx.me }, reason: 'no-plan', nodes };
  return {
    action: first,
    reason: `${plan.kind}:${plan.value.toFixed(1)}:gain=${plan.gain}`,
    nodes: nodes + refineNodes,
  };
}

function chooseStarter(ctx: EvalContext): Action {
  const starter: Side = ctx.personality.preferStart ? ctx.me : ctx.opp;
  return { type: 'CHOOSE_STARTER', side: ctx.me, starter };
}

function chooseEpitaph(v: MatchView, ctx: EvalContext, rng: Pcg32): Action {
  const s = determinize(v, rng);
  const choice = v.pendingEpitaphChoice;
  let keep = choice?.candidates[0]?.id ?? '';
  let best = -Infinity;
  for (const e of choice?.candidates ?? []) {
    if (choice?.kept.includes(e.id)) continue;
    const value = epitaphValue(s, ctx.me, e.inscription);
    if (value > best) {
      best = value;
      keep = e.id;
    }
  }
  return { type: 'CHOOSE_EPITAPH', side: ctx.me, keepEpitaphId: keep };
}

/**
 * Decides the next action for the viewing side. `legal` must be legalActions(state, view.viewer);
 * the returned action is always one of them.
 */
export function decide(v: MatchView, legal: readonly Action[], options: AiOptions): AiDecision {
  const difficulty = options.difficulty ?? 'standard';
  const personality = options.personality ?? BALANCED;
  const rng = Pcg32.seeded(parseHex64(options.seed), 0x5041574en);
  const choices = legal.filter((a) => a.type !== 'CONCEDE');
  if (choices.length === 0)
    return {
      action: legal[0] ?? { type: 'CONCEDE', side: v.viewer },
      reason: 'no-choice',
      nodes: 0,
    };
  if (choices.length === 1) return { action: choices[0]!, reason: 'only-choice', nodes: 0 };
  const ctx = contextFor(v, personality, difficulty);
  let result: { action: Action; reason: string; nodes: number };
  switch (v.phase) {
    case 'CHOOSE_STARTER':
      result = { action: chooseStarter(ctx), reason: 'starter', nodes: 0 };
      break;
    case 'TURN_START':
      result = passOrContinue(v, ctx, rng);
      break;
    case 'ACTION':
    case 'CHAIN':
      result = planAction(v, ctx, rng);
      break;
    case 'CHOOSE_INSCRIPTION': {
      const s = determinize(v, rng, allCards(v));
      result = {
        action: { type: 'CHOOSE_INSCRIPTION', side: ctx.me, replace: preferNewInscription(s, ctx) },
        reason: 'inscription',
        nodes: 0,
      };
      break;
    }
    case 'CHOOSE_EPITAPH':
      result = { action: chooseEpitaph(v, ctx, rng), reason: 'epitaph', nodes: 0 };
      break;
    default:
      result = { action: choices[0]!, reason: 'default', nodes: 0 };
  }
  // Safety net: only ever return a legal action (§20.1).
  const probe = determinize(v, Pcg32.seeded(1n, 1n));
  const keys = new Set(choices.map((a) => actionKey(probe, a)));
  if (!keys.has(actionKey(probe, result.action)))
    return { action: choices[0]!, reason: `fallback(${result.reason})`, nodes: result.nodes };
  return result;
}
